import { baseBetaBranches, baseProdBranches } from "@/features/constants"

import { THICK_V1_GRAPHQL_ENDPOINT } from "./constants"

/**
 * Resolve the thick-v1 GraphQL URL for the current branch.
 *
 * Resolution order (highest priority first):
 *   1. `VITE_THICK_V1_GRAPHQL_URL` — an explicit full URL override, used
 *      for local development against the raw indexer (e.g.
 *      `http://168.119.4.167:8001/graphql`). Returned as-is; no path
 *      appended.
 *   2. `VITE_BETA_SCALE_PROXY_API` / `VITE_PROD_SCALE_PROXY_API` — the
 *      production proxy base. `THICK_V1_GRAPHQL_ENDPOINT` is appended to
 *      form the full URL.
 *
 * Returns a fully-qualified URL so the caller never has to think about
 * paths.
 */
export function getThickV1GraphQlUrl(branch: string): string {
  const directOverride = import.meta.env.VITE_THICK_V1_GRAPHQL_URL
  if (directOverride) {
    return directOverride
  }

  let baseUrl = ""

  if (baseProdBranches.includes(branch)) {
    baseUrl = import.meta.env.VITE_PROD_SCALE_PROXY_API || ""
  }

  if (baseBetaBranches.includes(branch)) {
    baseUrl = import.meta.env.VITE_BETA_SCALE_PROXY_API || ""
  }

  if (import.meta.env.VITE_SCALE_PROXY_API) {
    baseUrl = import.meta.env.VITE_SCALE_PROXY_API || ""
  }

  if (!baseUrl) {
    throw new Error(`Branch "${branch}" not supported for thick-v1 GraphQL API`)
  }

  return `${baseUrl}${THICK_V1_GRAPHQL_ENDPOINT}`
}

/**
 * @deprecated Use `getThickV1GraphQlUrl` which returns the fully-qualified
 * URL (including the endpoint path) and supports `VITE_THICK_V1_GRAPHQL_URL`
 * as a direct override.
 *
 * Kept so that any call-sites I haven't yet migrated continue to work; it
 * now forwards to `getThickV1GraphQlUrl` and strips the trailing endpoint
 * path when necessary.
 */
export function getThickV1ProxyBaseUrl(branch: string): string {
  const fullUrl = getThickV1GraphQlUrl(branch)
  if (fullUrl.endsWith(THICK_V1_GRAPHQL_ENDPOINT)) {
    return fullUrl.slice(0, -THICK_V1_GRAPHQL_ENDPOINT.length)
  }
  return fullUrl
}
